"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError, type MeProfile } from "@/lib/api";

type Step = "dob" | "country" | "displayName" | "submitting" | "done";

/**
 * EPIC-050 STORY-2200 — auto-opening Tier 1 KYC modal.
 *
 * Drops onto any trade surface. On mount it fetches /v1/me and, if
 * the signed-in user's kyc_tier is 0, dims the page and walks them
 * through DOB -> Country -> Display Name. Submitting calls
 * POST /v1/me/kyc/tier1 (idempotent server-side) and reloads so
 * downstream /v1/me consumers pick up the new tier.
 *
 * Replaces the inline red "Identity details required (~2 minutes)"
 * banner that used to appear inside TradePanel — the gate now
 * surfaces BEFORE the user invests effort filling the order, in
 * the same shape as Claude.ai / Stripe onboarding.
 */
export function Kyc1AutoModal() {
  const [me, setMe] = useState<MeProfile | null>(null);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("dob");
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Portal-mount gate: render null on SSR + first paint so the
  // server/client trees match before useEffect promotes us.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Lazy-fetch the profile once the modal is asked to open. We don't
  // call /v1/me on every page mount any more — that was the old
  // auto-open behavior that blocked users from browsing the site.
  useEffect(() => {
    if (!open || me) return;
    let cancelled = false;
    api
      .me()
      .then((p) => {
        if (!cancelled) setMe(p);
      })
      .catch(() => {
        if (!cancelled) setOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, me]);

  // Listen for `granbay.kyc1.required` — fired by useKycGateRedirect
  // when any tier-1-gated action (place order, deposit, post, comment,
  // …) returns a 403. The modal only opens in those situations; free
  // navigation around the site is never blocked.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRequired = () => setOpen(true);
    window.addEventListener("granbay.kyc1.required", onRequired);
    return () =>
      window.removeEventListener("granbay.kyc1.required", onRequired);
  }, []);

  if (!mounted || !open) return null;
  // Profile still in-flight — show nothing rather than a flicker.
  if (!me) return null;
  // Already promoted — no-op (server is authoritative; this handles
  // the race where the user completed elsewhere mid-session).
  if (parseInt(me.kyc_tier, 10) >= 1) {
    setOpen(false);
    return null;
  }

  async function submit() {
    setErr(null);
    setStep("submitting");
    try {
      await api.submitKycTier1({
        dob,
        country: country || undefined,
        displayName: displayName || undefined,
      });
      setStep("done");
      // Refresh so every component reads the new tier from /v1/me.
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      // Server-side validation: map each known error code to the
      // step the user must fix + a clear message. Anything else
      // falls through to a generic retry on the current step.
      if (e instanceof ApiError) {
        if (
          e.code === "Tier1KycDisplayNameTaken" ||
          /display name.*taken/i.test(e.message)
        ) {
          setErr("That display name is already taken — try another.");
          setStep("displayName");
          return;
        }
        if (
          e.code === "Tier1KycAgeBelowMinimum" ||
          /at least 18/i.test(e.message)
        ) {
          setErr("You must be at least 18 to use Granbay.");
          setStep("dob");
          return;
        }
        if (
          e.code === "Tier1KycInvalidDob" ||
          /invalid.*dob|date.*invalid/i.test(e.message)
        ) {
          setErr("That date doesn't look right.");
          setStep("dob");
          return;
        }
        setErr(e.message || "Couldn't verify. Please try again.");
        setStep("displayName");
        return;
      }
      setErr("Couldn't verify. Please try again.");
      setStep("displayName");
    }
  }

  function next() {
    setErr(null);
    if (step === "dob") {
      // Strict YYYY-MM-DD shape + real-calendar validity. The bare
      // regex used to let "1989-08-89" through because /\d{2}/ matched
      // "89" — round-trip the parsed Date back to string and compare
      // to catch impossible day/month values.
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
      if (!m) {
        setErr("Enter your date of birth as YYYY-MM-DD.");
        return;
      }
      const [, yStr, moStr, dStr] = m;
      const y = Number(yStr);
      const mo = Number(moStr);
      const d = Number(dStr);
      const parsed = new Date(Date.UTC(y, mo - 1, d));
      const roundTrip =
        parsed.getUTCFullYear() === y &&
        parsed.getUTCMonth() === mo - 1 &&
        parsed.getUTCDate() === d;
      if (!roundTrip) {
        setErr("That date doesn't exist on the calendar.");
        return;
      }
      const now = new Date();
      if (parsed.getTime() > now.getTime()) {
        setErr("Your birthday can't be in the future.");
        return;
      }
      const ageMs = now.getTime() - parsed.getTime();
      const age = ageMs / (1000 * 60 * 60 * 24 * 365.25);
      if (age < 18) {
        setErr("You must be at least 18 to use Granbay.");
        return;
      }
      if (age > 120) {
        setErr("Please enter a realistic date of birth.");
        return;
      }
      setStep("country");
    } else if (step === "country") {
      setStep("displayName");
    } else if (step === "displayName") {
      void submit();
    }
  }

  const headings: Record<Step, string> = {
    dob: "When were you born?",
    country: "Where are you based?",
    displayName: "Pick a display name",
    submitting: "Verifying…",
    done: "Verified",
  };
  const subtitles: Record<Step, string | null> = {
    dob: "Granbay requires you to be 18+ to trade.",
    country: "Helps us show you the right markets and currency.",
    displayName: "How other traders will see you (optional).",
    submitting: null,
    done: null,
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kyc1-heading"
    >
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-2xl">
        <h2
          id="kyc1-heading"
          className="text-center text-[20px] font-bold text-[var(--color-text)]"
        >
          {headings[step]}
        </h2>
        {subtitles[step] ? (
          <p className="mt-1 text-center text-[13px] text-[var(--color-text2)]">
            {subtitles[step]}
          </p>
        ) : null}

        <div className="mt-6">
          {step === "dob" && (
            <input
              type="date"
              // 120-year floor + today ceiling. Some browsers (Safari)
              // still accept typed days like "89" — the round-trip
              // check in `next()` catches that, so this is the
              // first-line defense, not the only one.
              min={new Date(Date.now() - 120 * 365.25 * 86400_000)
                .toISOString()
                .slice(0, 10)}
              max={new Date().toISOString().slice(0, 10)}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              required
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-[14px] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              autoFocus
            />
          )}
          {step === "country" && (
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-[14px] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              autoFocus
            >
              <option value="">Select your country…</option>
              <option value="BR">Brazil</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
              <option value="IN">India</option>
              <option value="GB">United Kingdom</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
              <option value="other">Other</option>
            </select>
          )}
          {step === "displayName" && (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. trader_42 (optional)"
              maxLength={32}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-[14px] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              autoFocus
            />
          )}
        </div>

        {err ? (
          <p
            className="mt-3 text-center text-[12px]"
            style={{ color: "var(--color-no)" }}
          >
            {err}
          </p>
        ) : null}

        <button
          type="button"
          onClick={next}
          disabled={step === "submitting"}
          className="mt-6 w-full rounded-md bg-[var(--color-text)] py-3 text-[14px] font-bold text-[var(--color-bg)] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {step === "submitting"
            ? "Verifying…"
            : step === "displayName"
              ? "Continue & verify"
              : "Continue"}
        </button>

        <p className="mt-4 text-center text-[11px] text-[var(--color-text3)]">
          Email verified as {me.email}
        </p>
      </div>
    </div>,
    document.body,
  );
}
